import { useCallback, useContext } from 'react'

import {
  setPromoCode,
  apiPromoCodeInfoStart,
  apiPromoCodeInfoSuccess,
  apiPromoCodeInfoFailure,
  apiPromoCodeApplyStart,
  apiPromoCodeApplySuccess,
  apiPromoCodeApplyFailure,
  setPromoCodeRefusal
} from '../actions/promocodes'
import { useSelectors } from './selectors/use-selectors'
import { setMessage } from '../actions/settings'
import { context } from '../context'
import { AnalyticsService } from '../../services/analytics'
import { settings } from '../constants'
import { money } from '../../lib/gbp'
import { readJson, customerMessage } from '../../lib/api-json'
import { promoRefusal } from '../../lib/promo-credit'
const NOT_CHECKED = 'We could not check that code just now. Please try again.'

// "£25 off from Robin, on washes from £95." No em dashes in customer copy.
export const promoCodeMessage = ({ value, min_basket, from }) => {
  const who = from ? ` from ${from}` : ''
  const floor = min_basket ? ` on washes from ${money(min_basket)}` : ' on this order'
  return `${money(value)} off${who}${floor}.`
}

export const usePromoCodesApi = () => {
  const { dispatch } = useContext(context)
  const { promoCodeInfo } = useSelectors()

  // Checks a code without an account (a guest, or a ?promo_code= link). The
  // code is held as promoCodeInfo, applied on sign-in and posted with the
  // order. `via`: typed (the customer pressed Apply) or link.
  const apiPromoCodeInfo = useCallback(
    (code, { via = 'link' } = {}) => {
      const trimmed = (code || '').toString().trim()
      if (!trimmed) {
        dispatch(setPromoCodeRefusal(promoRefusal(null, '')))
        return
      }

      dispatch(apiPromoCodeInfoStart())
      fetch(`${settings.host}/api/v2/promo_codes/promo_code_info?code=${encodeURIComponent(trimmed)}`)
        .then(readJson)
        .then(json => {
          const { status } = json

          // Refused (expired, used up, unknown...): said under the code
          // field, and no money off (the reducer drops it if it was held).
          if (status === 'error') {
            const refusal = promoRefusal(json, trimmed)
            dispatch(apiPromoCodeInfoFailure(refusal.message))
            dispatch(setPromoCodeRefusal(refusal))
            AnalyticsService.trackPromoCodeRejected(json.data, via, trimmed)
            return
          }
          if (status !== 'ok') throw new Error(`promo_code_info: ${JSON.stringify(json)}`)

          const {
            data: { type, value, min_basket, from, code_type, code_id }
          } = json

          const info = { code: trimmed, type, value, min_basket, from, code_type, code_id }
          dispatch(setPromoCodeRefusal(null))
          dispatch(apiPromoCodeInfoSuccess(info))
          AnalyticsService.trackPromoCodeApplied(info, via)
          if (/^PercentPromoCode/.test(type)) {
            dispatch(setMessage(`Promo code applied. You will have ${value}% discount on this order.`, null, 'top', '#37b45a'))
          } else {
            dispatch(setMessage(promoCodeMessage({ value, min_basket, from }), null, 'top', '#37b45a'))
          }
        })
        .catch(error => {
          const message = customerMessage(error, NOT_CHECKED)

          if (!error?.customer) console.error('[apiPromoCodeInfo] error:', error)
          dispatch(apiPromoCodeInfoFailure(message))
          dispatch(setMessage(message, null, null, '#f44336'))
          AnalyticsService.trackPromoCodeRejected(error?.customer ? error.data : { refusal: 'not_checked' }, via, trimmed)
        })
    },
    [dispatch]
  )

  // Applies a code to the signed-in customer's account. Without a token
  // (a guest on Book & pay, who gets an account only when paying) the API
  // answers 401 with an empty body, so the code is checked instead and
  // applied at sign-up, exactly as a ?promo_code= link is.
  // `via`: typed, link or sign_in (a held code applied once signed in).
  const apiApplyPromoCode = useCallback(
    (code, via = 'typed') => {
      const trimmed = (code || '').toString().trim()
      const token = window.localStorage.getItem('token')
      if (!token) return apiPromoCodeInfo(trimmed, { via })
      if (!trimmed) {
        dispatch(setPromoCodeRefusal(promoRefusal(null, '')))
        return
      }

      const options = {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-User-Token': token
        },
        body: JSON.stringify({ code: trimmed })
      }

      dispatch(apiPromoCodeApplyStart())
      fetch(`${settings.host}/api/v2/promo_codes`, options)
        .then(response => {
          // A stale token: treat as a guest rather than show an error.
          if (response.status === 401) return { status: 'unauthorized' }
          return readJson(response)
        })
        .then(json => {
          const { status } = json

          if (status === 'unauthorized') {
            dispatch(apiPromoCodeApplyFailure(null))
            apiPromoCodeInfo(trimmed, { via })
            return
          }
          if (status === 'error') {
            const refusal = promoRefusal(json, trimmed)
            dispatch(apiPromoCodeApplyFailure(refusal.message))
            dispatch(setPromoCodeRefusal(refusal))
            AnalyticsService.trackPromoCodeRejected(json.data, via, trimmed)
            return
          }
          if (status !== 'ok') throw new Error(`promo_codes: ${JSON.stringify(json)}`)

          const {
            data: { credit, percent, deferred, code_type, code_id }
          } = json
          dispatch(setPromoCodeRefusal(null))

          // A referral or reward code (minimum basket): checked, not applied.
          // It stays as promoCodeInfo, is shown in the price summary when the
          // basket qualifies, and is posted with the order, where the server
          // applies it against the real basket.
          if (deferred) {
            dispatch(apiPromoCodeApplySuccess(credit))
            dispatch(apiPromoCodeInfoSuccess({ code: trimmed, ...deferred }))
            dispatch(setMessage(promoCodeMessage({ ...deferred }), null, 'top', '#37b45a'))
            AnalyticsService.trackPromoCodeApplied({ code: trimmed, ...deferred }, via)
            return
          }

          if (percent) {
            const { type, value } = percent

            dispatch(setPromoCode({ code: trimmed, type, value }))
            dispatch(apiPromoCodeApplySuccess(credit))
            !promoCodeInfo && dispatch(setMessage(`Promo code applied. You will have ${value}% discount on this order.`, null, 'top', '#37b45a'))
          } else {
            dispatch(apiPromoCodeApplySuccess(credit))
            !promoCodeInfo && dispatch(setMessage(`Promo code applied. You will have ${money(credit)} discount on this order.`, null, 'top', '#37b45a'))
          }
          dispatch(apiPromoCodeInfoSuccess(null))
          AnalyticsService.trackPromoCodeApplied(percent ? { code: trimmed, ...percent } : { code: trimmed, code_type, code_id }, via)
        })
        .catch(error => {
          const message = customerMessage(error, NOT_CHECKED)

          if (!error?.customer) console.error('[apiApplyPromoCode] error:', error)
          dispatch(apiPromoCodeApplyFailure(message))
          dispatch(setMessage(message, null, null, '#f44336'))
          AnalyticsService.trackPromoCodeRejected(error?.customer ? error.data : { refusal: 'not_checked' }, via, trimmed)
        })
    },
    [dispatch, promoCodeInfo, apiPromoCodeInfo]
  )

  // applyPromoCodeOnSignIn must be defined AFTER apiApplyPromoCode to avoid TDZ error
  const applyPromoCodeOnSignIn = useCallback(() => {
    if (!promoCodeInfo) return

    const { code } = promoCodeInfo

    if (!code) return

    apiApplyPromoCode(code, 'sign_in')
  }, [promoCodeInfo, apiApplyPromoCode])

  return {
    applyPromoCodeOnSignIn,
    apiPromoCodeInfo,
    apiApplyPromoCode
  }
}
